/**
 * G2eGente - Lista de Funcionários
 * Gerenciamento de funcionários da empresa
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI, employeeImportAPI } from '../../lib/api';
import { formatDateBR } from '../../lib/formatters';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  MoreVertical,
  Pencil,
  Trash2,
  Brain,
  ClipboardCheck,
  Loader2,
  UserCheck,
  Building2,
  Users,
  Filter,
  Mail,
  Smartphone,
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  History,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Bug,
  FileText,
  FileDown,
  Database,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';

const SITUACAO_OPTIONS = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  desligado: { label: 'Desligado', color: 'bg-red-100 text-red-700' },
  afastado: { label: 'Afastado', color: 'bg-amber-100 text-amber-700' },
  nao_avaliar: { label: 'Não Avaliar', color: 'bg-slate-100 text-slate-700' }
};

// For legacy support
const STATUS_OPTIONS = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  inativo: { label: 'Inativo', color: 'bg-slate-100 text-slate-700' },
  afastado: { label: 'Afastado', color: 'bg-amber-100 text-amber-700' }
};

export default function EmployeeList() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { selectedCompany, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Obter ID para chave do localStorage (selectedCompany para multi-empresa, ou user.id para empresa única)
  const filterStorageKey = selectedCompany?.company_id || user?.id;
  
  // Estados dos filtros
  const [filterDept, setFilterDeptState] = useState('all');
  const [filterSituacao, setFilterSituacaoState] = useState('ativo');
  const [filterGestor, setFilterGestorState] = useState('all');
  
  // Ref para tracking da empresa/usuário atual
  const currentStorageKeyRef = useRef(null);
  
  // Funções para atualizar filtros e salvar no localStorage
  const setFilterDept = (value) => {
    setFilterDeptState(value);
    if (filterStorageKey) {
      localStorage.setItem(`employee_filter_${filterStorageKey}_dept`, value);
    }
  };
  
  const setFilterSituacao = (value) => {
    setFilterSituacaoState(value);
    if (filterStorageKey) {
      localStorage.setItem(`employee_filter_${filterStorageKey}_situacao`, value);
    }
  };
  
  const setFilterGestor = (value) => {
    setFilterGestorState(value);
    if (filterStorageKey) {
      localStorage.setItem(`employee_filter_${filterStorageKey}_gestor`, value);
    }
  };
  
  // Restaurar filtros do localStorage quando disponível
  useEffect(() => {
    // Se não há chave de armazenamento, não fazer nada
    if (!filterStorageKey) return;
    
    // Se é a mesma chave, não restaurar novamente (evitar loop)
    if (currentStorageKeyRef.current === filterStorageKey) return;
    
    // Nova chave - restaurar filtros
    currentStorageKeyRef.current = filterStorageKey;
    
    const storedDept = localStorage.getItem(`employee_filter_${filterStorageKey}_dept`);
    const storedSituacao = localStorage.getItem(`employee_filter_${filterStorageKey}_situacao`);
    const storedGestor = localStorage.getItem(`employee_filter_${filterStorageKey}_gestor`);
    
    // Aplicar valores armazenados ou usar defaults
    setFilterDeptState(storedDept || 'all');
    setFilterSituacaoState(storedSituacao || 'ativo');
    setFilterGestorState(storedGestor || 'all');
  }, [filterStorageKey]);
  
  const [deleteEmployee, setDeleteEmployee] = useState(null);
  const [permanentDeleteEmployee, setPermanentDeleteEmployee] = useState(null);
  const [inviteEmployee, setInviteEmployee] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [deepDiagnosing, setDeepDiagnosing] = useState(false);
  const [deepDiagnosticResult, setDeepDiagnosticResult] = useState(null);
  const [showDeepDiagnosticModal, setShowDeepDiagnosticModal] = useState(false);
  const [debugLogResult, setDebugLogResult] = useState(null);
  const [showDebugLogModal, setShowDebugLogModal] = useState(false);
  const [debugLogging, setDebugLogging] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Recarrega dados quando a empresa selecionada mudar
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [selectedCompany?.company_id]);

  const fetchData = async () => {
    try {
      // Passa o company_id explicitamente para garantir contexto correto
      const companyId = selectedCompany?.company_id;
      const [empRes, deptRes] = await Promise.all([
        employeesAPI.getAll({}, companyId),
        employeesAPI.getDepartments(companyId)
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data.departments || []);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteEmployee) return;
    try {
      await employeesAPI.delete(deleteEmployee.id);
      toast.success('Funcionário desligado com sucesso');
      setEmployees(employees.map(e => 
        e.id === deleteEmployee.id ? { ...e, situacao: 'desligado', status: 'desligado' } : e
      ));
    } catch (error) {
      toast.error('Erro ao desligar funcionário');
    } finally {
      setDeleteEmployee(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteEmployee) return;
    setDeleting(true);
    try {
      const res = await employeesAPI.deletePermanent(permanentDeleteEmployee.id);
      toast.success(res.data.message || 'Cadastro excluído permanentemente');
      setEmployees(employees.filter(e => e.id !== permanentDeleteEmployee.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cadastro');
    } finally {
      setPermanentDeleteEmployee(null);
      setDeleting(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmployee) return;
    setInviting(true);
    try {
      const res = await employeesAPI.invite(inviteEmployee.id);
      toast.success(
        <div>
          <p className="font-semibold">Convite enviado!</p>
          <p className="text-sm">Senha temporária: <code className="bg-slate-100 px-1 rounded">{res.data.temp_password}</code></p>
          <p className="text-xs text-slate-500 mt-1">Anote e informe ao funcionário</p>
        </div>,
        { duration: 10000 }
      );
      setEmployees(employees.map(e => 
        e.id === inviteEmployee.id ? { ...e, user_id: 'invited' } : e
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao convidar funcionário');
    } finally {
      setInviting(false);
      setInviteEmployee(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await employeeImportAPI.downloadTemplate();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_funcionarios.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado com sucesso');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await employeeImportAPI.importCSV(file);
      setImportResult(res.data);
      
      const { imported, updated, skipped } = res.data;
      const total = imported + updated;
      
      if (total > 0) {
        let msg = [];
        if (imported > 0) msg.push(`${imported} novo(s)`);
        if (updated > 0) msg.push(`${updated} atualizado(s)`);
        toast.success(`Funcionário(s): ${msg.join(', ')}`);
        fetchData(); // Refresh list
      } else if (skipped > 0) {
        toast.info(`${skipped} funcionário(s) sem alterações necessárias`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDiagnose = async () => {
    setDiagnosing(true);
    try {
      const res = await employeesAPI.diagnose();
      setDiagnosticResult(res.data);
      setShowDiagnosticModal(true);
      
      if (res.data.funcionarios_com_problemas === 0) {
        toast.success('Todos os funcionários estão com dados completos!');
      } else {
        toast.warning(`${res.data.funcionarios_com_problemas} funcionário(s) com dados incompletos`);
      }
    } catch (error) {
      toast.error('Erro ao diagnosticar funcionários');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleFixIncomplete = async () => {
    setFixing(true);
    try {
      // Primeiro corrigir campos obrigatórios (cargo, nome, etc)
      const res1 = await employeesAPI.fixRequiredFields();
      console.log('Fix required fields:', res1.data);
      
      // Depois corrigir situacao e outros
      const res2 = await employeesAPI.fixIncomplete();
      
      const totalFixed = (res1.data.correcoes || 0) + (res2.data.correcoes || 0);
      toast.success(`${totalFixed} correção(ões) aplicada(s)`);
      setShowDiagnosticModal(false);
      setDiagnosticResult(null);
      fetchData(); // Refresh list
    } catch (error) {
      console.error('Erro ao corrigir:', error);
      toast.error('Erro ao corrigir funcionários');
    } finally {
      setFixing(false);
    }
  };

  const handleViewRawData = async () => {
    try {
      const res = await employeesAPI.getRawData();
      console.log('RAW DATA:', res.data);
      
      // Mostrar em um alert com os dados
      const dataStr = JSON.stringify(res.data, null, 2);
      
      // Criar e baixar arquivo
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raw_data_funcionarios_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      if (res.data.com_problemas > 0) {
        toast.error(`${res.data.com_problemas} funcionário(s) com problemas detectados! Veja o arquivo baixado.`);
      } else {
        toast.success('Dados baixados. Nenhum problema encontrado nos dados.');
      }
    } catch (error) {
      console.error('Erro ao obter dados RAW:', error);
      toast.error('Erro ao obter dados');
    }
  };

  // Exportar funcionários para Excel
  const handleExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      
      // Preparar dados para exportação
      const dataToExport = filteredEmployees.map(emp => ({
        'Nome': emp.nome || '',
        'CPF': emp.cpf || '',
        'Email': emp.email || '',
        'Telefone': emp.telefone || '',
        'Cargo': emp.cargo || '',
        'Código Cargo': emp.codigo_cargo || '',
        'Departamento': emp.departamento || '',
        'Setor': emp.setor || '',
        'Tipo Contrato': emp.tipo_contrato || '',
        'Situação': emp.situacao || '',
        'Data Admissão': formatDateBR(emp.data_admissao),
        'Gestor': emp.gestor_nome || '',
        'Salário': emp.salario || '',
        'Centro de Custo': emp.centro_custo || '',
        'Matrícula': emp.matricula || '',
        'Data Cadastro': formatDateBR(emp.created_at)
      }));
      
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Funcionários');
      
      // Ajustar largura das colunas
      const colWidths = Object.keys(dataToExport[0] || {}).map(key => ({ wch: Math.max(key.length, 15) }));
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, `funcionarios_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Planilha exportada com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar planilha');
    }
  };

  // Exportar funcionários para PDF
  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let y = 20;
      
      // Título
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Lista de Funcionários', margin, y);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, margin, y + 6);
      pdf.text(`Total: ${filteredEmployees.length} funcionário(s)`, margin, y + 11);
      
      y += 20;
      
      // Cabeçalho da tabela
      const headers = ['Nome', 'CPF', 'Cargo', 'Cód.', 'Departamento', 'Situação', 'Admissão'];
      const colWidths = [50, 35, 45, 20, 40, 25, 25];
      
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(margin, y, pageWidth - margin * 2, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      
      let x = margin + 2;
      headers.forEach((header, i) => {
        pdf.text(header, x, y + 5.5);
        x += colWidths[i];
      });
      
      y += 10;
      pdf.setTextColor(50, 50, 50);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      // Linhas de dados
      filteredEmployees.forEach((emp, index) => {
        if (y > pageHeight - 20) {
          pdf.addPage();
          y = 20;
        }
        
        // Alternar cor de fundo
        if (index % 2 === 0) {
          pdf.setFillColor(248, 250, 252);
          pdf.rect(margin, y - 4, pageWidth - margin * 2, 7, 'F');
        }
        
        x = margin + 2;
        const row = [
          (emp.nome || '').substring(0, 28),
          emp.cpf || '',
          (emp.cargo || '').substring(0, 22),
          (emp.codigo_cargo || '').substring(0, 10),
          (emp.departamento || '').substring(0, 18),
          emp.situacao || '',
          formatDateBR(emp.data_admissao)
        ];
        
        row.forEach((cell, i) => {
          pdf.text(String(cell), x, y);
          x += colWidths[i];
        });
        
        y += 7;
      });
      
      // Rodapé
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 5);
        pdf.text('G2eGente - Gestão de Pessoas', margin, pageHeight - 5);
      }
      
      pdf.save(`funcionarios_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleDeepDiagnose = async () => {
    setDeepDiagnosing(true);
    try {
      const res = await employeesAPI.deepDiagnose();
      setDeepDiagnosticResult(res.data);
      setShowDeepDiagnosticModal(true);
      
      if (res.data.com_erro === 0) {
        toast.success('Todos os funcionários carregam corretamente!');
      } else {
        toast.error(`${res.data.com_erro} funcionário(s) com erros de carregamento`);
      }
    } catch (error) {
      console.error('Erro no diagnóstico profundo:', error);
      toast.error('Erro ao executar diagnóstico profundo');
    } finally {
      setDeepDiagnosing(false);
    }
  };

  const handleDebugLog = async (employeeId, employeeName) => {
    setDebugLogging(true);
    try {
      const res = await employeesAPI.getDebugLog(employeeId);
      setDebugLogResult({
        ...res.data,
        employee_name: employeeName,
        employee_id: employeeId
      });
      setShowDebugLogModal(true);
      
      if (res.data.success) {
        toast.success('Log gerado com sucesso!');
      } else {
        toast.error('Erro encontrado no carregamento');
      }
    } catch (error) {
      console.error('Erro ao gerar log:', error);
      toast.error('Erro ao gerar log de debug');
      setDebugLogResult({
        success: false,
        employee_name: employeeName,
        employee_id: employeeId,
        log: [{
          timestamp: new Date().toISOString(),
          step: "ERRO API",
          status: "ERRO",
          error: error.response?.data?.detail || error.message
        }],
        erro_final: error.response?.data?.detail || error.message
      });
      setShowDebugLogModal(true);
    } finally {
      setDebugLogging(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = !search || 
      emp.nome.toLowerCase().includes(search.toLowerCase()) ||
      emp.cargo.toLowerCase().includes(search.toLowerCase()) ||
      emp.email?.toLowerCase().includes(search.toLowerCase()) ||
      emp.cpf?.includes(search);
    
    const matchesDept = filterDept === 'all' || emp.departamento === filterDept;
    const empSituacao = emp.situacao || emp.status || 'ativo';
    const matchesSituacao = filterSituacao === 'all' || empSituacao === filterSituacao;
    
    // Filtro por gestor
    const matchesGestor = filterGestor === 'all' || 
      (filterGestor === 'sem_gestor' && !emp.gestor_id) ||
      emp.gestor_id === filterGestor;
    
    return matchesSearch && matchesDept && matchesSituacao && matchesGestor;
  });

  // Extrair gestores únicos dos funcionários
  const gestoresUnicos = React.useMemo(() => {
    const gestorMap = new Map();
    employees.forEach(emp => {
      if (emp.gestor_id && emp.gestor_nome) {
        gestorMap.set(emp.gestor_id, emp.gestor_nome);
      }
    });
    // Também adicionar funcionários que são gestores de alguém
    employees.forEach(emp => {
      const subordinados = employees.filter(e => e.gestor_id === emp.id);
      if (subordinados.length > 0 && !gestorMap.has(emp.id)) {
        gestorMap.set(emp.id, emp.nome);
      }
    });
    return Array.from(gestorMap.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [employees]);

  const stats = {
    total: employees.length,
    ativos: employees.filter(e => (e.situacao || e.status) === 'ativo').length,
    comDISC: employees.filter(e => e.disc_profile).length
  };

  // Get situacao display info
  const getSituacaoDisplay = (emp) => {
    const situacao = emp.situacao || emp.status || 'ativo';
    return SITUACAO_OPTIONS[situacao] || STATUS_OPTIONS[situacao] || { label: situacao, color: 'bg-slate-100 text-slate-700' };
  };

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="employee-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Funcionários</h1>
            <p className="text-slate-600">Gerencie sua equipe</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Exportar Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-employees-btn">
                  <FileDown className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Importar Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={importing}>
                  {importing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Importar Cadastros
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Template CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Importar Arquivo CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportCSV}
              accept=".csv"
              className="hidden"
            />
            <Button 
              onClick={() => navigate('/company/employees/new')}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="new-employee-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Funcionário
            </Button>
          </div>
        </div>

        {/* Import Result Alert */}
        {importResult && importResult.errors?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">
                    {importResult.imported} importado(s), {importResult.errors.length} erro(s)
                  </p>
                  <ul className="mt-2 text-sm text-amber-700 space-y-1">
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>• ... e mais {importResult.errors.length - 5} erro(s)</li>
                    )}
                  </ul>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2 text-amber-700"
                    onClick={() => setImportResult(null)}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Ativos</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.ativos}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-600">Com DISC</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.comDISC}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome, cargo, email ou CPF..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-full sm:w-48" data-testid="filter-dept">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Departamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterGestor} onValueChange={setFilterGestor}>
            <SelectTrigger className="w-full sm:w-52" data-testid="filter-gestor">
              <UserCog className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Gestor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Gestores</SelectItem>
              <SelectItem value="sem_gestor">Sem Gestor</SelectItem>
              {gestoresUnicos.map(gestor => (
                <SelectItem key={gestor.id} value={gestor.id}>
                  {gestor.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterSituacao} onValueChange={setFilterSituacao}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-situacao">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Situação" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="desligado">Desligados</SelectItem>
              <SelectItem value="afastado">Afastados</SelectItem>
              <SelectItem value="nao_avaliar">Não Avaliar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Employee List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : filteredEmployees.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {employees.length === 0 ? 'Nenhum funcionário cadastrado' : 'Nenhum resultado encontrado'}
              </h3>
              <p className="text-slate-600 mb-6">
                {employees.length === 0 
                  ? 'Comece cadastrando seus funcionários para gerenciar avaliações'
                  : 'Tente ajustar os filtros de busca'}
              </p>
              {employees.length === 0 && (
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    onClick={() => navigate('/company/employees/new')}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Cadastrar Funcionário
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar CSV
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredEmployees.map((employee) => {
              const situacaoDisplay = getSituacaoDisplay(employee);
              return (
                <Card 
                  key={employee.id} 
                  className="border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`employee-item-${employee.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                          {employee.foto_url ? (
                            <img src={employee.foto_url} alt={employee.nome} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <span className="text-cyan-700 font-semibold text-lg">
                              {employee.nome.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-slate-900">{employee.nome}</h3>
                            <Badge className={situacaoDisplay.color}>
                              {situacaoDisplay.label}
                            </Badge>
                            {employee.disc_profile && (
                              <Badge className="bg-purple-100 text-purple-700">
                                <Brain className="w-3 h-3 mr-1" />
                                {employee.disc_profile}
                              </Badge>
                            )}
                          </div>
                          <p className="text-slate-600">{employee.cargo}</p>
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                            {employee.cpf && (
                              <span className="font-mono text-xs">
                                CPF: {employee.cpf}
                              </span>
                            )}
                            {employee.departamento && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5" />
                                {employee.departamento}
                              </span>
                            )}
                            {employee.email && (
                              <span>{employee.email}</span>
                            )}
                            {employee.gestor_nome && (
                              <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                Gestor: {employee.gestor_nome}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Invite button - only show if no user_id and has email */}
                        {!employee.user_id && employee.email && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setInviteEmployee(employee)}
                            className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          >
                            <Smartphone className="w-4 h-4 mr-1" />
                            Convidar
                          </Button>
                        )}
                        {employee.user_id && (
                          <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                            <Smartphone className="w-3 h-3 mr-1" />
                            App
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/company/employees/${employee.id}/disc`)}
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          <Brain className="w-4 h-4 mr-1" />
                          DISC
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/company/employees/${employee.id}/evaluate`)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <ClipboardCheck className="w-4 h-4 mr-1" />
                          Avaliar
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-5 h-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/company/employees/${employee.id}`)}>
                              <UserCheck className="w-4 h-4 mr-2" />
                              Ver Perfil
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/company/employees/${employee.id}/edit`)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/company/employees/${employee.id}/history`)}>
                              <History className="w-4 h-4 mr-2" />
                              Histórico de Avaliações
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDebugLog(employee.id, employee.nome)}
                              className="text-amber-600"
                            >
                              <Bug className="w-4 h-4 mr-2" />
                              Ver Log de Debug
                            </DropdownMenuItem>
                            {!employee.user_id && employee.email && (
                              <DropdownMenuItem onClick={() => setInviteEmployee(employee)}>
                                <Mail className="w-4 h-4 mr-2" />
                                Enviar Convite
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setDeleteEmployee(employee)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Desligar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-700 font-medium"
                              onClick={() => setPermanentDeleteEmployee(employee)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir Cadastro
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteEmployee} onOpenChange={() => setDeleteEmployee(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Desligar Funcionário</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja desligar o funcionário "{deleteEmployee?.nome}"? 
                O funcionário será marcado como desligado mas seus dados serão mantidos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Desligar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Permanent Delete Confirmation Dialog */}
        <AlertDialog open={!!permanentDeleteEmployee} onOpenChange={() => setPermanentDeleteEmployee(null)}>
          <AlertDialogContent className="border-red-200">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-700 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Excluir Cadastro Permanentemente
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p className="font-medium text-red-600">
                  ⚠️ Esta ação é IRREVERSÍVEL!
                </p>
                <p>
                  Tem certeza que deseja excluir permanentemente o cadastro de <strong>"{permanentDeleteEmployee?.nome}"</strong>?
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-red-700 mb-2">Serão excluídos permanentemente:</p>
                  <ul className="list-disc list-inside text-red-600 space-y-1">
                    <li>Todos os dados cadastrais do funcionário</li>
                    <li>Histórico de movimentações de cargo</li>
                    <li>Atribuições de atividades</li>
                    <li>Avaliações DISC</li>
                    <li>Treinamentos realizados</li>
                    <li>Estágios probatórios</li>
                    <li>Avaliações de desempenho</li>
                    <li>Planos de desenvolvimento (PDI)</li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handlePermanentDelete}
                disabled={deleting}
                className="bg-red-700 hover:bg-red-800"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Excluir Permanentemente'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Invite Confirmation Dialog */}
        <AlertDialog open={!!inviteEmployee} onOpenChange={() => setInviteEmployee(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-cyan-600" />
                Convidar para o App
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Criar conta de acesso ao app para <strong>{inviteEmployee?.nome}</strong>?</p>
                <p className="text-sm">
                  O funcionário receberá uma senha temporária e poderá acessar:
                </p>
                <ul className="text-sm list-disc list-inside text-slate-600">
                  <li>Suas avaliações de desempenho</li>
                  <li>Fazer autoavaliações</li>
                  <li>Realizar teste DISC</li>
                </ul>
                <p className="text-xs text-slate-500 mt-2">
                  Email: {inviteEmployee?.email}
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={inviting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleInvite}
                disabled={inviting}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {inviting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enviar Convite
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Diagnostic Modal */}
        <AlertDialog open={showDiagnosticModal} onOpenChange={setShowDiagnosticModal}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                {diagnosticResult?.funcionarios_com_problemas === 0 ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                Diagnóstico de Dados
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Total de Funcionários</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {diagnosticResult?.total_funcionarios || 0}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${
                      diagnosticResult?.funcionarios_com_problemas === 0 
                        ? 'bg-green-50' 
                        : 'bg-amber-50'
                    }`}>
                      <p className="text-xs text-slate-500">Com Dados Incompletos</p>
                      <p className={`text-2xl font-bold ${
                        diagnosticResult?.funcionarios_com_problemas === 0 
                          ? 'text-green-600' 
                          : 'text-amber-600'
                      }`}>
                        {diagnosticResult?.funcionarios_com_problemas || 0}
                      </p>
                    </div>
                  </div>

                  {/* Análise de Situação */}
                  {diagnosticResult?.analise_situacao && (
                    <div className="border rounded-lg p-3 bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Análise de Situação (valores encontrados):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(diagnosticResult.analise_situacao).map(([situacao, count]) => {
                          // Verificar se o valor está EXATAMENTE nos valores válidos (case sensitive)
                          const isExactMatch = diagnosticResult.situacoes_validas?.includes(situacao);
                          return (
                            <span 
                              key={situacao}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                isExactMatch 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {situacao}: {count} {!isExactMatch && '⚠️ (não normalizado)'}
                            </span>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Valores válidos (exatos): {diagnosticResult.situacoes_validas?.join(', ')}
                      </p>
                      {diagnosticResult?.situacao_nao_normalizada > 0 && (
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
                          <p className="text-sm text-amber-700">
                            ⚠️ <strong>{diagnosticResult.situacao_nao_normalizada}</strong> funcionário(s) com situação não normalizada.
                            Clique em "Normalizar Situação" para corrigir.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {diagnosticResult?.funcionarios_com_problemas > 0 && (
                    <div className="border rounded-lg p-3 max-h-60 overflow-y-auto">
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Funcionários com problemas:
                      </p>
                      <div className="space-y-2">
                        {diagnosticResult?.detalhes?.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="font-medium text-slate-800">{item.nome}</p>
                            <p className="text-xs text-slate-500">{item.email || 'Sem email'} • {item.cargo || 'Sem cargo'}</p>
                            <p className="text-xs text-amber-700 mt-1">
                              Problemas: {item.problemas?.join(', ') || item.campos_faltando?.join(', ')}
                            </p>
                            {item.situacao_atual && (
                              <p className="text-xs text-slate-500">
                                Situação atual: "{item.situacao_atual}"
                              </p>
                            )}
                          </div>
                        ))}
                        {diagnosticResult?.detalhes?.length > 10 && (
                          <p className="text-xs text-slate-500 text-center">
                            ... e mais {diagnosticResult.detalhes.length - 10} funcionário(s)
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {diagnosticResult?.funcionarios_com_problemas === 0 && diagnosticResult?.situacao_nao_normalizada === 0 && (
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">
                        Todos os funcionários estão com dados completos e normalizados!
                      </p>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Fechar</AlertDialogCancel>
              {(diagnosticResult?.funcionarios_com_problemas > 0 || diagnosticResult?.situacao_nao_normalizada > 0) && (
                <AlertDialogAction 
                  onClick={handleFixIncomplete}
                  disabled={fixing}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {fixing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Wrench className="w-4 h-4 mr-2" />
                  )}
                  {diagnosticResult?.situacao_nao_normalizada > 0 
                    ? `Normalizar Situação (${diagnosticResult.situacao_nao_normalizada})` 
                    : 'Corrigir Automaticamente'}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Deep Diagnostic Modal - Log Detalhado */}
        <AlertDialog open={showDeepDiagnosticModal} onOpenChange={setShowDeepDiagnosticModal}>
          <AlertDialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-red-600" />
                Log Detalhado de Erros
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Total de Funcionários</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {deepDiagnosticResult?.total_funcionarios || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-slate-500">Carregam OK</p>
                      <p className="text-2xl font-bold text-green-600">
                        {deepDiagnosticResult?.sucesso || 0}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${
                      deepDiagnosticResult?.com_erro === 0 
                        ? 'bg-green-50' 
                        : 'bg-red-50'
                    }`}>
                      <p className="text-xs text-slate-500">Com Erro</p>
                      <p className={`text-2xl font-bold ${
                        deepDiagnosticResult?.com_erro === 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {deepDiagnosticResult?.com_erro || 0}
                      </p>
                    </div>
                  </div>

                  {deepDiagnosticResult?.com_erro > 0 && (
                    <div className="border rounded-lg p-3 max-h-96 overflow-y-auto bg-slate-900 text-slate-100 font-mono text-xs">
                      <p className="text-red-400 font-bold mb-3 text-sm">
                        === ERROS DETALHADOS ===
                      </p>
                      <div className="space-y-4">
                        {deepDiagnosticResult?.erros_detalhados?.map((item, idx) => (
                          <div key={idx} className="border-b border-slate-700 pb-3">
                            <p className="text-cyan-400">
                              [{idx + 1}] ID: {item.id}
                            </p>
                            <p className="text-yellow-400">
                              Nome: {item.nome}
                            </p>
                            <p className="text-slate-400">
                              Email: {item.email || 'N/A'}
                            </p>
                            <p className="text-red-400 mt-1">
                              ERRO: {item.erro}
                            </p>
                            <details className="mt-2">
                              <summary className="cursor-pointer text-slate-500 hover:text-slate-300">
                                Ver dados raw...
                              </summary>
                              <pre className="mt-1 text-slate-500 whitespace-pre-wrap break-all">
                                {JSON.stringify(item.dados_raw, null, 2)}
                              </pre>
                            </details>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {deepDiagnosticResult?.com_erro === 0 && (
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">
                        Todos os funcionários carregam corretamente!
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        Nenhum erro de serialização encontrado.
                      </p>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Fechar</AlertDialogCancel>
              {deepDiagnosticResult?.com_erro > 0 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const logText = JSON.stringify(deepDiagnosticResult, null, 2);
                    const blob = new Blob([logText], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `diagnostico_funcionarios_${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Baixar Log
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Debug Log Modal - Log Individual de Funcionário */}
        <AlertDialog open={showDebugLogModal} onOpenChange={setShowDebugLogModal}>
          <AlertDialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-amber-600" />
                Log de Debug - {debugLogResult?.employee_name}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4 mt-4">
                  {/* Status Summary */}
                  <div className={`p-4 rounded-lg ${debugLogResult?.success ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-2">
                      {debugLogResult?.success ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${debugLogResult?.success ? 'text-green-700' : 'text-red-700'}`}>
                        {debugLogResult?.success ? 'Carregamento bem-sucedido' : 'Erro no carregamento'}
                      </span>
                    </div>
                    {debugLogResult?.erro_final && (
                      <p className="mt-2 text-sm text-red-600 font-mono bg-red-100 p-2 rounded">
                        {debugLogResult.erro_final}
                      </p>
                    )}
                  </div>

                  {/* Log Timeline */}
                  <div className="border rounded-lg bg-slate-900 text-slate-100 font-mono text-xs max-h-96 overflow-y-auto p-4">
                    <p className="text-cyan-400 font-bold mb-4 text-sm">
                      === LOG DE CARREGAMENTO ===
                    </p>
                    <p className="text-slate-500 mb-4">
                      Funcionário ID: {debugLogResult?.employee_id}
                    </p>
                    
                    {debugLogResult?.log?.map((entry, idx) => (
                      <div key={idx} className={`mb-3 pb-3 border-b border-slate-700 ${
                        entry.status === 'ERRO' ? 'text-red-400' :
                        entry.status === 'OK' ? 'text-green-400' :
                        entry.status === 'SUCESSO' ? 'text-emerald-400' :
                        entry.status === 'APLICADO PADRÃO' ? 'text-yellow-400' :
                        'text-slate-300'
                      }`}>
                        <div className="flex items-start gap-2">
                          <span className="text-slate-500 whitespace-nowrap">
                            {new Date(entry.timestamp).toLocaleTimeString('pt-BR')}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            entry.status === 'ERRO' ? 'bg-red-900' :
                            entry.status === 'OK' ? 'bg-green-900' :
                            entry.status === 'SUCESSO' ? 'bg-emerald-900' :
                            entry.status === 'APLICADO PADRÃO' ? 'bg-yellow-900' :
                            'bg-slate-700'
                          }`}>
                            {entry.status}
                          </span>
                          <span className="text-white">{entry.step}</span>
                        </div>
                        
                        {entry.error && (
                          <p className="mt-1 ml-20 text-red-400">
                            ERRO: {entry.error}
                          </p>
                        )}
                        
                        {entry.data && Object.keys(entry.data).length > 0 && (
                          <details className="mt-1 ml-20">
                            <summary className="cursor-pointer text-slate-500 hover:text-slate-300">
                              Ver dados...
                            </summary>
                            <pre className="mt-1 text-slate-400 whitespace-pre-wrap break-all text-xs">
                              {JSON.stringify(entry.data, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Dados do Funcionário (se carregou) */}
                  {debugLogResult?.dados_funcionario && (
                    <div className="border rounded-lg p-3 bg-slate-50">
                      <p className="font-medium text-slate-700 mb-2">Dados Carregados:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>ID:</strong> {debugLogResult.dados_funcionario.id}</div>
                        <div><strong>Nome:</strong> {debugLogResult.dados_funcionario.nome}</div>
                        <div><strong>Cargo:</strong> {debugLogResult.dados_funcionario.cargo}</div>
                        <div><strong>Email:</strong> {debugLogResult.dados_funcionario.email || 'N/A'}</div>
                        <div><strong>Departamento:</strong> {debugLogResult.dados_funcionario.departamento || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Fechar</AlertDialogCancel>
              <Button
                variant="outline"
                onClick={() => {
                  const logText = JSON.stringify(debugLogResult, null, 2);
                  const blob = new Blob([logText], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `debug_log_${debugLogResult?.employee_id}_${new Date().toISOString().split('T')[0]}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Baixar Log Completo
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
